import { debounce } from 'lodash';

import { ApiResponse } from 'common/api/api-wrapper';
import { ReactComponent as Regenerate } from 'common/assets/images/ai-generate.svg';
import { ReactComponent as NoActivityIcon } from 'common/assets/images/NoActivity.svg';
import AIFeedback from 'common/components/AIFeedback/AIFeedback';
import { FEEDBACK_OBJECT_TYPE, MODULE_NAME } from 'common/constants';
import { AI_SIMULATION_GENAI_DISCLAIMER } from 'common/constants/AISimulationConstants';
import {
	HORIZON_ADD_RELATION_DATA,
	RELATION_MAPPING_TAB_VALUES,
} from 'common/constants/TaxonomyConstants';
import { TOAST_MESSAGE_STATUS } from 'common/constants/ToastMessageConstants';
import {
	setIsRelationshipMappingDeleted,
	setToastMessage,
} from 'common/store/reducer/commonReducer';

import AddMappingModal from 'features/RiskProfile/Taxonomy/RelationMapping/AddMappingModal/AddMappingModal';
import { fetchAllTabsData } from 'features/RiskProfile/Taxonomy/TaxonomyUtils';

import {
	useCallback,
	useEffect,
	useState,
	Typography,
	Tab,
	Box,
	styled,
	Grid,
	Stack,
	InputAdornment,
	TabContext,
	TabList,
	TabPanel,
	capitalize,
	useSelector,
	useDispatch,
	setActionPlanTabIndex,
	Plus,
	SortIcon,
	SideDrawer,
	CardTable,
	AIMappingCard,
	DetailedMappingLayout,
	addNewHorizonMapping,
	updateTaskWorkflow,
	MODULES,
	SORT_ORDER,
	TABLE_SIZE_12,
	ButtonComp,
	capitalizeEachWord,
	HORIZON_PROGRESS_BAR_ELEMENTS,
	HTTP_STATUS_CODES,
	AlertComponent,
	APP_GENERAL_ERROR_MSG,
	StyledTextField,
	SearchOutlined,
	TAB_TASKS,
	ENTITY_STATUS,
	tableAnnonationDataTaskData,
	RELATED_OBJECT_TYPE,
	setSelectedTabValue,
	ActionPlanTaskTable,
	getRelationshipMappingData,
	ComplianceAcceptanceModal,
	SyntheticEvent,
	RootState,
	IMappingEnity,
	IRelationshipMappingPayload,
	IStyledTabProps,
	AnnotationType,
	toSentenceCase,
	getRelationshipForObligation,
	useTableQuery,
	styles,
	generateAIRelMappingCards,
} from './ActionPlanLayoutModules';
import { Loader } from './ActionPlanTaskTableModules';

const {
	focustabColor,
	tabsTextColor,
	tabSelectedTextColor,
	whiteTextColor,
	selectedTabBackground,
	tabBorderRadius,
	addNewButtonColor,
	tabBackground,
} = styles;

const { PENDING_REVIEW } = ENTITY_STATUS;
const { NONE } = RELATION_MAPPING_TAB_VALUES;
const { HORIZON_SCAN_NAME } = MODULE_NAME;
const { TOAST_SUCCESS } = TOAST_MESSAGE_STATUS;

const style = {
	'.MuiButtonBase-root': {
		backgroundColor: tabBackground,
		border: 'none',
	},
	'.MuiButtonBase-root.MuiTab-root.Mui-selected': {
		color: tabSelectedTextColor,
	},
	'.Mui-selected': {
		background: `${focustabColor} !important`,
		color: tabsTextColor,
		borderRadius: tabBorderRadius,
	},
	'.MuiTabs-flexContainer': {
		backgroundColor: `${tabBackground} !important`,
	},
	'.MuiTabs-indicator': {
		display: 'none',
	},
};

const StyledTab = styled((props: IStyledTabProps) => (
	<Tab disableRipple {...props} />
))({
	textTransform: 'none',
	fontSize: '16px',
	color: tabsTextColor,
	borderRadius: tabBorderRadius,
	padding: '4px 12px',
	'&.Mui-selected': {
		color: whiteTextColor,
		background: selectedTabBackground,
	},
	'& .MuiTabs-indicator': {
		backgroundColor: selectedTabBackground,
	},
	'&.Mui-focusVisible': {
		backgroundColor: selectedTabBackground,
	},
});

const { HORIZON_SCAN_AND_ALERTS } = MODULES;
const { HORIZON } = RELATED_OBJECT_TYPE;
const {
	APPLICABILITY_ASSESSMENT,
	IMPACT_ANALYSIS,
	COMPLETE,
	NOTSTARTED,
	COMPLIANCE_ACCEPTANCE,
	ACTION_PLAN,
} = HORIZON_PROGRESS_BAR_ELEMENTS;
const { ASCENDING, DESCENDING } = SORT_ORDER;
const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;
const { OK, CREATED } = HTTP_STATUS_CODES;

/**
 * ActionPlanLayout component renders the layout for the action plan section.
 * It displays the relationship mapping data, allows searching and filtering, and provides options for giving feedback.
 * @component
 * @param {object} props - The component props.
 * @param {string} props.id - The ID of the action plan.
 * @param {function} props.makeAPICall - The function to make an API call.
 * @param {object} props.data - The data for the action plan.
 * @param {string} props.moduleType - The module type of the action plan.
 * @param {string} props.regId - The ID of the regulation.
 * @returns {JSX.Element} The ActionPlanLayout component.
 */
const ActionPlanLayout = (props: any) => {
	const { id, makeAPICall, data, moduleType, regId } = props;
	const isHorizonScanModule = moduleType === HORIZON_SCAN_AND_ALERTS;
	const dispatch = useDispatch();
	const payloadArray: any[] = [];

	const sideDrawerState = useSelector(
		(state: RootState) => state.common.mappingDrawer,
	);

	const [openModal, setOpenModal] = useState<boolean>(false);
	const [sideDrawerOpen, setIsSideDrawerOpen] =
		useState<boolean>(sideDrawerState);
	const [relationshipData, setRelationshipData] = useState<IMappingEnity[]>([]);
	const [releationshipDataCount, setRelationshipDataCount] =
		useState<number>(0);
	const [relationshipMappingLoading, setRelationshipMappingLoading] =
		useState<boolean>(true);
	const [sortOrder, setSortOrder] = useState<string>('asc');
	const [searchText, setSearchText] = useState<string>('');
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [isAddingRelationMapping, setIsAddingRelationMapping] =
		useState<boolean>(false);
	const [isRegenerateMappingLoading, setIsRegenerateMappingLoading] =
		useState<boolean>(false);
	const [shouldResetFeedback, setShouldResetFeedback] =
		useState<boolean>(false);
	const [isAllEntityDataLoading, setIsAllEntityDataLoading] =
		useState<boolean>(true);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const isRelationshipMappingDeleted: boolean = useSelector(
		(state: RootState) => state?.common?.isRelationshipMappingDeleted,
	);

	const taskTableMappedData = useSelector(
		(state: RootState) => state.common.taskTableMappedData,
	);

	const selectedTabValue: string = useSelector(
		(state: RootState) => state?.mappingDetails?.selectedTabValue,
	);

	const currentWorkflowStatus = useSelector(
		(state: RootState) => state.horizonScan.currentWorkflowStatus,
	);

	const actionPlanTabIndex: string = useSelector(
		(state: RootState) => state?.horizonScan?.actionPlanTabIndex,
	);

	const { tableQueryObj, updateTableQuery } = useTableQuery({
		id: 'horizonRelationshipMappingCard',
		defaultPageSize: TABLE_SIZE_12,
	});

	const riskRefTaxonomySourceFiltersKey = useSelector(
		(state: RootState) => state.taxonomy.riskRefTaxonomySourceFiltersKey,
	);

	const debouncedSearchHandler = useCallback(
		debounce((value: string) => {
			setSearchText(value);
			const pageObj = {
				pageSize: TABLE_SIZE_12,
				pageNumber: 0,
			};
			const { pageNumber, pageSize } = tableQueryObj;
			updateTableQuery(pageObj);
			fetchRelationshipMappingData({
				searchText: value,
				pageNumber,
				pageSize,
			});
		}, 300),
		[],
	);

	const textFieldValueChanged = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const { value } = event.target;
		if (selectedTabValue !== TAB_TASKS) {
			debouncedSearchHandler(value);
		} else {
			setSearchText(value);
		}
	};

	const fetchRelationshipMappingData = async (
		data: IRelationshipMappingPayload = {
			pageNumber: tableQueryObj.pageNumber,
			pageSize: tableQueryObj.pageSize,
		},
	) => {
		const { pageNumber, pageSize } = data;
		setRelationshipMappingLoading(true);
		const payload = {
			sortOrder,
			sortParam: isHorizonScanModule ? 'renderOrder,childName' : 'parentType',
			searchText,
			pageNumber: pageNumber || 0,
			pageSize: pageSize || 0,
		};
		const res = isHorizonScanModule
			? await getRelationshipMappingData(id, payload)
			: await getRelationshipForObligation(id, regId, payload);
		if (res?.statusCode === OK && res.responseBody) {
			newMappingData(res.responseBody.content);
			setRelationshipDataCount(res.responseBody.totalCount);
		} else {
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setRelationshipMappingLoading(false);
		setIsRegenerateMappingLoading(false);
	};

	useEffect(() => {
		const { pageNumber, pageSize } = tableQueryObj;
		fetchRelationshipMappingData({
			searchText,
			pageNumber,
			pageSize,
		});
	}, [
		id,
		searchText,
		sideDrawerState,
		selectedTabValue,
		tableQueryObj?.pageNumber,
		tableQueryObj?.pageSize,
	]);

	useEffect(() => {
		if (
			(isRelationshipMappingDeleted && !relationshipData?.length) ||
			(isRelationshipMappingDeleted && !taskTableMappedData?.length)
		) {
			updateTableQuery({ ...tableQueryObj, pageNumber: 0 });
		}
		return () => {
			dispatch(setIsRelationshipMappingDeleted(false));
		};
	}, [isRelationshipMappingDeleted, relationshipData, taskTableMappedData]);

	const newMappingData = (mappingData: any) => {
		const data: any = [];
		mappingData.forEach((item: any) => {
			data.push({
				childType: item.childType,
				id: item.childId,
				aiScore: item?.aiScore ? capitalize(item.aiScore) : '',
				reviewStatus: item.reviewStatus,
				businessUnit: item.businessUnit,
				title: item.childName,
				childId: item.childId,
				childName: item.childName,
				parentId: item.parentId,
				parentType: item?.parentType,
				generateMode: item?.generateMode,
				createdBy: item?.createdBy,
			});
		});
		setRelationshipData(data);
	};

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const handleTabChange = useCallback(
		(event: SyntheticEvent, newValue: string) => {
			const { pageNumber, pageSize } = tableQueryObj;
			dispatch(setActionPlanTabIndex(newValue));
			dispatch(setSelectedTabValue(newValue));
			fetchRelationshipMappingData({
				searchText,
				pageNumber,
				pageSize,
			});
		},
		[searchText, tableQueryObj?.pageNumber, tableQueryObj?.pageSize],
	);

	const onSubmit = () => {
		setOpenModal(true);
	};

	const { RELATIONSHIP_MAPPING } = FEEDBACK_OBJECT_TYPE;

	const onAddBtnClickHandler = async (selectedOptions: any) => {
		setIsAddingRelationMapping(true);
		selectedOptions?.map((option: any) => {
			const data = {
				parentObjectId: id,
				parentObjectType: HORIZON,
				impactObjectId: option?.id,
				impactObjectType: option?.entityType,
				loggedInUser: loggedInUser?.userId,
				reviewStatus: PENDING_REVIEW,
			};
			payloadArray?.push(data);
			return payloadArray;
		});
		const newEntityObj: ApiResponse<any, any> = await addNewHorizonMapping(
			payloadArray,
		);
		setIsAddingRelationMapping(false);
		modalCloseHandler();
		fetchAllTabsData(
			NONE,
			riskRefTaxonomySourceFiltersKey,
			setIsAllEntityDataLoading,
			dispatch,
			id,
		);
		if (newEntityObj?.statusCode === CREATED) {
			dispatch(
				setToastMessage({
					message: toSentenceCase('The mapping has been successfully done'),
					severity: TOAST_SUCCESS,
					isToastOpen: true,
				}),
			);
			fetchRelationshipMappingData();
		} else {
			dispatch(
				setToastMessage({
					message: newEntityObj?.errorBody?.errorMessage,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const handlePageChange = (data: any) => {
		fetchRelationshipMappingData(data);
		updateTableQuery(data);
	};

	const handleClose = () => {
		setOpenModal(false);
	};

	const handleAcceptSubmit = async () => {
		const acceptComplianceData = {
			taskRequestList: [
				{
					id: data?.taskId,
					status: COMPLETE,
					assignedObjectId: data?.id,
					assignedObjectType: HORIZON,
				},
			],
		};
		const taskCompleteRes = await updateTaskWorkflow(acceptComplianceData);
		if (taskCompleteRes?.statusCode === OK) {
			makeAPICall(true);
		}
	};

	/**
	 * @description method to trigger add mapping button is clicked
	 */
	const addMappingClickHandler = () => {
		setIsModalOpen(true);
	};

	/**
	 * @description method to close the add mapping modal
	 */
	const modalCloseHandler = () => {
		setIsModalOpen(false);
		const { pageNumber, pageSize } = tableQueryObj;
		fetchRelationshipMappingData({
			searchText,
			pageNumber,
			pageSize,
		});
	};

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);
	const sortButtonClicked = () => {
		if (sortOrder === ASCENDING) {
			setSortOrder(DESCENDING);
		} else if (sortOrder === DESCENDING) {
			setSortOrder(ASCENDING);
		}
		updateTableQuery({ ...tableQueryObj, pageNumber: 0 });
		fetchRelationshipMappingData();
	};
	const isFeedbackDisabled =
		isHorizonScanModule && data?.assignedUserId !== loggedInUser?.userId;

	const displayCardTable = () =>
		relationshipData?.length > 0 ? (
			<div>
				<Grid className={styles['card-layout-style']}>
					<CardTable
						id="horizonRelationshipMappingCard"
						handlePagination={handlePageChange}
						render={(rowData) => (
							<AIMappingCard
								cardData={rowData}
								sideDrawerHandler={() => {
									setIsSideDrawerOpen(true);
								}}
								isFromHorizonScan={isHorizonScanModule}
								enabled={
									currentWorkflowStatus.toLowerCase() ===
										IMPACT_ANALYSIS.toLowerCase() ||
									currentWorkflowStatus.toLowerCase() ===
										ACTION_PLAN.toLowerCase()
								}
							/>
						)}
						rows={relationshipData}
						totalItems={releationshipDataCount}
						defaultQuery={tableQueryObj}
					/>

					{isAIRelationshipMappingAvailable && (
						<div className={styles['disclaimer-wrapper']}>
							<div className={styles['disclaimer-inner-wrapper']}>
								<Typography className={styles['disclaimer-text']}>
									{DISCLAIMER}
								</Typography>
							</div>
							<AIFeedback
								objectType={RELATIONSHIP_MAPPING}
								module={HORIZON_SCAN_AND_ALERTS}
								objectData={{
									prompt: data?.informationText,
									response: relationshipData,
									reference: id,
								}}
								isDisabled={isFeedbackDisabled}
								shouldResetFeedback={shouldResetFeedback}
							/>
						</div>
					)}
				</Grid>
			</div>
		) : (
			<div className={styles['no-activity-img-wrapper']}>
				<Typography className={styles['no-activity-img']}>
					<NoActivityIcon />
				</Typography>
				<p>{toSentenceCase('No Relationship Mapping Found')}</p>
			</div>
		);
	const isAddNewButtonEnabled =
		isHorizonScanModule &&
		actionPlanTabIndex !== 'tasks' &&
		currentWorkflowStatus === IMPACT_ANALYSIS &&
		data?.assignedUserId === loggedInUser?.userId;
	// condition to check if regenerate button is enabled
	const isRegenerateButtonEnabled =
		isHorizonScanModule &&
		actionPlanTabIndex !== 'tasks' &&
		(currentWorkflowStatus === APPLICABILITY_ASSESSMENT ||
			currentWorkflowStatus === IMPACT_ANALYSIS) &&
		data?.assignedUserId === loggedInUser?.userId;

	// condition to check if any AI relationship mapping is available
	const isAIRelationshipMappingAvailable = relationshipData.some(
		(rel: IMappingEnity) => rel.generateMode === 'AI',
	);

	// when regenerate button is clicked
	const regenerateButtonClicked = async () => {
		updateTableQuery({ ...tableQueryObj, pageNumber: 0 });
		setIsRegenerateMappingLoading(true);
		const generateResponse = await generateAIRelMappingCards(id);
		if (generateResponse?.statusCode === OK) {
			setShouldResetFeedback(true);
			fetchRelationshipMappingData();
		} else {
			dispatch(
				setToastMessage({
					message: 'Failed to generate AI Relationship Mapping',
					severity: 'error',
					isToastOpen: true,
				}),
			);
			setIsRegenerateMappingLoading(false);
		}
	};

	/* To fetch all tab data in add mapping section */
	useEffect(() => {
		fetchAllTabsData(
			NONE,
			riskRefTaxonomySourceFiltersKey,
			setIsAllEntityDataLoading,
			dispatch,
			id,
		);
	}, []);

	return (
		<>
			<Grid container>
				<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
					<TabContext value={selectedTabValue}>
						<Box
							className={styles['action-plan-header-container']}
							display="flex"
							flexDirection={isHorizonScanModule ? 'column' : 'row'}
							alignItems={isHorizonScanModule ? 'normal' : 'center'}
						>
							<Box>
								<Typography
									className={
										isHorizonScanModule
											? styles['action-plan-title-horizon']
											: styles['action-plan-title']
									}
								>
									{toSentenceCase('Action Plan')}
								</Typography>
							</Box>
							<Box
								className={styles['tab-container']}
								display="flex"
								alignItems="center"
								justifyContent="space-between"
							>
								<TabList
									sx={style}
									onChange={handleTabChange}
									className={styles['panel-layout']}
								>
									<StyledTab
										label={toSentenceCase('Relationship Mapping')}
										value="mapping"
									/>
									<StyledTab
										label="Tasks"
										value="tasks"
										disabled={
											isHorizonScanModule &&
											(currentWorkflowStatus.toLowerCase() ===
												APPLICABILITY_ASSESSMENT.toLowerCase() ||
												currentWorkflowStatus.toLowerCase() ===
													NOTSTARTED.toLowerCase())
										}
									/>
								</TabList>
								<Box display="flex" alignItems="center">
									{isRegenerateButtonEnabled && (
										<Box mr={2}>
											<ButtonComp
												btnType="secondary"
												compClass={styles['mapping-regenerate-button']}
												startIcon={
													<Box
														className={styles['mapping-regenerate-button-icon']}
													>
														<Regenerate />
													</Box>
												}
												clickHandler={regenerateButtonClicked}
												data-testid="regenerate-button"
												disabled={isRegenerateMappingLoading}
											>
												{isAIRelationshipMappingAvailable
													? 'Regenerate'
													: 'Generate'}
											</ButtonComp>
										</Box>
									)}
									{isHorizonScanModule &&
										actionPlanTabIndex !== 'tasks' &&
										currentWorkflowStatus === IMPACT_ANALYSIS &&
										data?.assignedUserId === loggedInUser?.userId && (
											<Box mr={2}>
												<div className={styles['add-new-dropdown-wrapper']}>
													<ButtonComp
														btnType="secondary"
														startIcon={<Plus fill={addNewButtonColor} />}
														onClick={addMappingClickHandler}
														compClass={styles['btn-add-mapping']}
														data-testid="add-mapping-btn"
													>
														{toSentenceCase('Add Mapping')}
													</ButtonComp>
												</div>
											</Box>
										)}
									<Box mr={2}>
										<StyledTextField
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<SearchOutlined
															className={styles['input-search-icon']}
														/>
													</InputAdornment>
												),
												inputProps: { 'data-testid': 'search-field' },
											}}
											className={styles['search-input']}
											fullWidth={false}
											placeholder="Search"
											size="small"
											onChange={textFieldValueChanged}
											value={searchText}
										/>
									</Box>
									{selectedTabValue !== TAB_TASKS && (
										<ButtonComp
											compClass={styles.btn}
											btnType="text"
											clickHandler={sortButtonClicked}
											data-testid="tab-sort-icon"
											disabled={!relationshipData?.length}
										>
											<Box>
												<SortIcon data-testid="sort-button" />
											</Box>
										</ButtonComp>
									)}
								</Box>
							</Box>
						</Box>
						{selectedTabValue === TAB_TASKS && (
							<Grid item xs={8}>
								<div className={styles['graph-color-annotation']}>
									{tableAnnonationDataTaskData.map((row: AnnotationType) => (
										<div key={row.iconLabel} className={styles[row.labelClass]}>
											<div className={styles[row.iconClass]} />
											<span
												className={styles[row.iconLabelClass]}
												data-testid={row.iconLabelTestid}
											>
												{row.iconLabel}
											</span>
										</div>
									))}
								</div>
							</Grid>
						)}
						<Box>
							<TabPanel value="mapping" className={styles['tab-panel-style']}>
								{relationshipMappingLoading ? (
									<Loader show className={styles['loading-icon']} />
								) : (
									displayCardTable()
								)}
							</TabPanel>
							<TabPanel value="tasks" className={styles['task-table']}>
								<ActionPlanTaskTable
									subPartId={id}
									data={data}
									sideDrawerHandler={() => setIsSideDrawerOpen(true)}
									searchTask={searchText}
									moduleType={moduleType}
								/>
							</TabPanel>
						</Box>
					</TabContext>
				</Grid>
				{isModalOpen && (
					<AddMappingModal
						isModalOpen={isModalOpen}
						modalCloseHandler={modalCloseHandler}
						parentEntityType={NONE}
						childEntityOptionsData={HORIZON_ADD_RELATION_DATA}
						onAddBtnClickHandler={(selectedOptions: any) =>
							onAddBtnClickHandler(selectedOptions)
						}
						moduleType={HORIZON_SCAN_NAME}
						isAddingRelationMapping={isAddingRelationMapping}
						isAllEntityDataLoading={isAllEntityDataLoading}
					/>
				)}
				{sideDrawerOpen && (
					<div>
						<SideDrawer
							className={styles['side-drawer']}
							drawerState={sideDrawerState}
						>
							<DetailedMappingLayout
								moduleType={moduleType}
								subModuleType={
									isHorizonScanModule ? '' : HORIZON_SCAN_AND_ALERTS
								}
								isDeleteEnabled={isAddNewButtonEnabled}
							/>
						</SideDrawer>
					</div>
				)}

				<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
					<Stack
						direction="row"
						justifyContent="flex-end"
						spacing={2}
						sx={{ margin: '8px' }}
					>
						{isHorizonScanModule && currentWorkflowStatus === COMPLETE && (
							<ButtonComp
								compClass={styles['ac-accepted-style']}
								btnType="primary"
								disabled
								clickHandler={onSubmit}
								data-testid="submit-btn"
							>
								{toSentenceCase('Compliance Accepted')}
							</ButtonComp>
						)}
						{isHorizonScanModule &&
							capitalizeEachWord(currentWorkflowStatus) ===
								COMPLIANCE_ACCEPTANCE &&
							data?.assignedUserId === loggedInUser?.userId && (
								<ButtonComp
									compClass={styles['ac-button-style']}
									btnType="primary"
									clickHandler={onSubmit}
									data-testid="submit-btn"
								>
									{toSentenceCase('Accept Compliance')}
								</ButtonComp>
							)}
					</Stack>
				</Grid>
				{openModal && (
					<ComplianceAcceptanceModal
						openModal={openModal}
						handleClose={handleClose}
						handleSubmit={handleAcceptSubmit}
					/>
				)}
			</Grid>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</>
	);
};

export default ActionPlanLayout;
